import { useCallback, useMemo } from 'react';
import { useFormikContext } from 'formik';

import { EMPTY_STRING } from '@e-socna/empty';
import { getFragmentData, GroupingModeEnum } from '@e-socna/data-layer';

import { useIdFactory } from '../../../hooks/useIdFactory.hook';

import { ResolvableServiceUsageForm_ResolvableServiceUsage_Fragment } from '../../ResolvableServiceUsage/fragments/resolvable-service-usage.fragment';
import type { ResolvableServiceUsagesGroupForm_ResolvableServiceUsagesGroup } from '../../ResolvableServiceUsage/fragments/resolvable-service-usages-group.fragment';

import {
  createGroup,
  mergeGroupsIntoSingleton,
  moveServiceUsageToGroup,
  removeGroup,
  updateGroup,
  reorderGroups,
  switchToManualGrouping,
} from '../utils/service-usages-groups.util';
import type {
  GroupMutations,
  MoveToGroupMutation,
  ServiceUserMutationCallbacks,
  SwitchGroupingModeMutation,
} from '../types/props.type';

interface Params {
  readonly groups:
    | readonly ResolvableServiceUsagesGroupForm_ResolvableServiceUsagesGroup[]
    | null
    | undefined;
  readonly name: string;
}

// Form-state counterpart of the group mutations, used before the service user
// exists (creation wizard): the whole structure is sent with the creation input.
export const useFormGroupMutations = ({ groups, name }: Readonly<Params>) => {
  const { setFieldValue } = useFormikContext();
  const { generateId } = useIdFactory();

  const formGroups = useMemo(
    () =>
      (groups ?? []).map((group) => ({
        ...group,
        serviceUsages: getFragmentData(
          ResolvableServiceUsageForm_ResolvableServiceUsage_Fragment,
          group.serviceUsages,
        ),
      })),
    [groups],
  );

  const apply = useCallback(
    (
      transform: () => unknown,
      { onError, onSuccess }: ServiceUserMutationCallbacks,
    ) => {
      try {
        void setFieldValue(name, transform());
      } catch (error) {
        onError(error);

        return;
      }

      onSuccess();
    },
    [name, setFieldValue],
  );

  const switchGroupingMode = useCallback<SwitchGroupingModeMutation>(
    ({ groupingMode, label }, callbacks) => {
      apply(
        () =>
          groupingMode === GroupingModeEnum.Manual
            ? switchToManualGrouping({
                groups: formGroups,
                label: label ?? EMPTY_STRING,
                newGroupId: generateId(),
              })
            : mergeGroupsIntoSingleton({
                groups: formGroups,
                newGroupId: generateId(),
              }),
        callbacks,
      );
    },
    [apply, formGroups, generateId],
  );

  const moveToGroup = useCallback<MoveToGroupMutation>(
    ({ serviceUsageId, targetServiceUsagesGroupId }, callbacks) => {
      apply(
        () =>
          moveServiceUsageToGroup({
            groups: formGroups,
            serviceUsageId,
            targetGroupId: targetServiceUsagesGroupId,
          }),
        callbacks,
      );
    },
    [apply, formGroups],
  );

  const groupMutations = useMemo<GroupMutations>(
    () => ({
      createGroup: ({ isOptional, label }, callbacks) => {
        apply(
          () =>
            createGroup({
              groups: formGroups,
              isOptional,
              label,
              newGroupId: generateId(),
            }),
          callbacks,
        );
      },
      removeGroup: ({ serviceUsagesGroupId }, callbacks) => {
        apply(
          () =>
            removeGroup({ groupId: serviceUsagesGroupId, groups: formGroups }),
          callbacks,
        );
      },
      reorderGroups: ({ groups: orderedGroups }, callbacks) => {
        apply(
          () =>
            reorderGroups({
              groups: formGroups,
              orderedGroupIds: orderedGroups.map((group) => group.groupId),
            }),
          callbacks,
        );
      },
      updateGroup: ({ isOptional, label, serviceUsagesGroupId }, callbacks) => {
        apply(
          () =>
            updateGroup({
              groupId: serviceUsagesGroupId,
              groups: formGroups,
              isOptional,
              label,
            }),
          callbacks,
        );
      },
    }),
    [apply, formGroups, generateId],
  );

  return useMemo(
    () => ({ groups: groupMutations, moveToGroup, switchGroupingMode }),
    [groupMutations, moveToGroup, switchGroupingMode],
  );
};
